import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterLink} from '@angular/router';
import {NgxDatatableModule} from '@swimlane/ngx-datatable';
import {ToastrService} from 'ngx-toastr';
import {BreadcrumbService} from '@cdss/services/breadcrumb.service';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {OverlaySpinnerComponent} from '@shared/components/overlay-spinner/overlay-spinner.component';
import {ContractsService, isAlreadyNotified} from '@shared/api/services/v2/contracts.service';
import {
    Contract,
    ContractListFilters,
    contractDatePart,
    isContractDeactivated,
    isContractDeleted
} from '@shared/api/models/query/contract.model';
import {Pagination, PaginationResponse} from '@models/pagination.model';
import {Permissions} from '@models/permissions.enum';
import {PermissionRolesMap} from '@models/permissions.db';
import {PermissionPipe} from '@components/directives/permission.pipe';
import {AuthService} from '@shared/services/security/auth.service';
import {CONTRACT_NEW_ROUTE, contractDetailRoute} from '@shared/services/router/contract-routes';
import {ContractsFilterComponent} from './filter/contracts-filter.component';
import {ContractsFilterDto} from './filter/contracts-filter.dto';

/**
 * UNIVAPLAT-3515: admin contracts list (route `cdss/admin/contracts`, VIEW_CONTRACTS).
 * General admin sees "All contracts", a contract admin "My contracts" (the API scopes the
 * data; the UI only hides what that role cannot use). No contract data is logged.
 */
@Component({
    selector: 'cdss-contracts-list-page',
    imports: [
        CommonModule,
        RouterLink,
        NgxDatatableModule,
        SpinnerComponent,
        OverlaySpinnerComponent,
        ContractsFilterComponent,
        PermissionPipe,
    ],
    standalone: true,
    templateUrl: './contracts-list.page.html',
    styleUrls: ['./contracts-list.page.scss']
})
export class ContractsListPage implements OnInit {
    readonly Permissions = Permissions;
    readonly newContractRoute = '/' + CONTRACT_NEW_ROUTE;

    loading = true;
    refreshing = false;

    pagination: Pagination = new Pagination(25);
    rows: Contract[] = [];

    filter: ContractsFilterDto = new ContractsFilterDto();
    showFilters = false;

    @ViewChild('filtersButton') filtersButton?: ElementRef<HTMLButtonElement>;

    constructor(
        private breadcrumbService: BreadcrumbService,
        private toastr: ToastrService,
        private contractsService: ContractsService,
        private authService: AuthService,
    ) {
    }

    /** General admin signal: only they hold CREATE_CONTRACT. */
    get isGeneralAdmin(): boolean {
        const userRoles = this.authService.roles ?? [];
        return (PermissionRolesMap[Permissions.CREATE_CONTRACT] ?? []).some(role => userRoles.includes(role));
    }

    get heading(): string {
        return this.isGeneralAdmin ? 'All contracts' : 'My contracts';
    }

    ngOnInit(): void {
        this.breadcrumbService.set([
            {title: 'Contracts', url: null}
        ]);
        this.refreshRows();
    }

    detailRoute(row: Contract): string {
        return '/' + contractDetailRoute(row.id);
    }

    formatDate(value: string | null | undefined): string {
        if (!value) {
            return '-';
        }
        const part = contractDatePart(value);
        return part ? `${part.slice(8, 10)}/${part.slice(5, 7)}/${part.slice(0, 4)}` : '-';
    }

    statusLabel(row: Contract): string | null {
        if (isContractDeleted(row)) {
            return 'Deleted';
        }
        return isContractDeactivated(row) ? 'Deactivated' : null;
    }

    toggleFilters(): void {
        this.showFilters = !this.showFilters;
        if (!this.showFilters) {
            this.restoreFocus();
        }
    }

    onFilterApply(): void {
        this.refreshing = true;
        this.pagination.start = 0;
        this.refreshRows();
        this.showFilters = false;
        this.restoreFocus();
    }

    onFilterCancel(): void {
        this.showFilters = false;
        this.restoreFocus();
    }

    onSort(event: any): void {
        this.refreshing = true;
        this.pagination.sort = {prop: event.sorts[0].prop, order: event.sorts[0].dir};
        this.refreshRows();
    }

    setPaginationSize(size: number): void {
        this.refreshing = true;
        this.pagination.size = size;
        this.pagination.start = 0;
        this.refreshRows();
    }

    previousPage(): void {
        this.refreshing = true;
        this.pagination.start = this.pagination.currentPage - 2;
        this.refreshRows();
    }

    nextPage(): void {
        this.refreshing = true;
        this.pagination.start = this.pagination.currentPage;
        this.refreshRows();
    }

    async refreshRows(): Promise<void> {
        try {
            const response: PaginationResponse<Contract> = await this.contractsService.list(this.pagination, this.buildFilters());
            const hasData = !!response?.data?.length;
            this.rows = response?.data || [];
            this.pagination.totalCount = hasData ? response.pagination.totalCount : 0;
            this.pagination.fromRecord = hasData ? response.pagination.fromRecord : 0;
            this.pagination.toRecord = hasData ? response.pagination.toRecord : 0;
            this.pagination.currentPage = hasData ? response.pagination.currentPage : 0;
            this.pagination.totalPages = hasData ? response.pagination.totalPages : 0;
        } catch (error) {
            // Backend owns the error text; nothing from the response is logged.
            if (!isAlreadyNotified(error)) {
                this.toastr.error('Failed to load contracts');
            }
        } finally {
            this.loading = false;
            this.refreshing = false;
        }
    }

    private buildFilters(): ContractListFilters {
        const filters: ContractListFilters = {};
        if (this.filter.title) filters.title = this.filter.title;
        if (this.filter.code) filters.code = this.filter.code;
        if (this.filter.serviceProvider) filters.serviceProvider = this.filter.serviceProvider;
        if (this.isGeneralAdmin) {
            if (this.filter.showDeactivated) filters.showDeactivated = true;
            if (this.filter.showDeleted) filters.showDeleted = true;
        }
        return filters;
    }

    /** Return focus to the Filters button once the overlay is gone. */
    private restoreFocus(): void {
        setTimeout(() => this.filtersButton?.nativeElement.focus());
    }
}
